// ScoreLync basketball scoreboard themes — five color panels that match
// what a tenant admin can pick in the SPA. Each carries the four colors
// used by the layout: panel bg, accent (header strips), border, label,
// and sub-label.

export type ThemeKey =
  | 'classic'
  | 'maroon'
  | 'navy'
  | 'forest'
  | 'royal'
  | 'arena-led'
  | 'premium-glass'
  | 'angular'
  | 'daylight' | 'electric' | 'mono' | 'retro-led' | 'broadcast'
  | 'stadium-gold' | 'carbon' | 'sunset' | 'frost' | 'volt';

export interface Theme {
  name: string;
  bg: string;
  ac: string;
  br: string;
  lbl: string;
  sub: string;
}

export const THEMES: Record<ThemeKey, Theme> = {
  classic: {
    name: 'Classic Red',
    bg: '#8b1a1a',
    ac: '#a02828',
    br: '#5c1010',
    lbl: '#ffffff',
    sub: '#ffcccc',
  },
  maroon: {
    name: 'Maroon',
    bg: '#4a1028',
    ac: '#5c1838',
    br: '#380820',
    lbl: '#ffffff',
    sub: '#e8c0d0',
  },
  navy: {
    name: 'Navy',
    bg: '#1a2744',
    ac: '#223358',
    br: '#101830',
    lbl: '#ffffff',
    sub: '#b0c4de',
  },
  forest: {
    name: 'Forest',
    bg: '#1a3a2a',
    ac: '#224a38',
    br: '#102818',
    lbl: '#ffffff',
    sub: '#b0d8c0',
  },
  royal: {
    name: 'Royal',
    bg: '#1a3a8a',
    ac: '#2244a0',
    br: '#102868',
    lbl: '#ffffff',
    sub: '#b0c8ff',
  },
  'arena-led':      { name:'Arena LED',     bg:'#0a0c10', ac:'#11151c', br:'#1c2330',   lbl:'#ffffff', sub:'#9fb3d1' },
  'premium-glass':  { name:'Premium Glass', bg:'#0a0f1c', ac:'#16203a', br:'#ffffff22', lbl:'#eaf2ff', sub:'#9ec5ff' },
  'angular':        { name:'Angular',       bg:'#0c1018', ac:'#161b28', br:'#232a3c',   lbl:'#ffffff', sub:'#7aa2ff' },
  daylight:         { name:'Daylight',      bg:'#eef2f8', ac:'#ffffff', br:'#dde4ee',   lbl:'#16213a', sub:'#5b6b86' },
  electric:         { name:'Electric',      bg:'#0c163a', ac:'#28468f', br:'#78b4ff66', lbl:'#f4faff', sub:'#acd2ff' },
  mono:             { name:'Mono',          bg:'#fafbfc', ac:'#ffffff', br:'#e6e9ef',   lbl:'#0e1116', sub:'#6b7280' },
  'retro-led':      { name:'Retro LED',     bg:'#000000', ac:'#0a0a0a', br:'#1a1a1a',   lbl:'#ffb000', sub:'#7a5a00' },
  broadcast:        { name:'Broadcast',     bg:'#0c1220', ac:'#1a2436', br:'#3a4658',   lbl:'#ffffff', sub:'#9fb0c8' },
  'stadium-gold':   { name:'Stadium Gold',  bg:'#06140e', ac:'#0a2118', br:'#d4af3755', lbl:'#ffffff', sub:'#b8c8bc' },
  carbon:           { name:'Carbon',        bg:'#15171c', ac:'#1d2128', br:'#363c46',   lbl:'#ffffff', sub:'#9aa3b0' },
  sunset:           { name:'Sunset',        bg:'#ff7e5f', ac:'#fff1e8', br:'#e0a88a',   lbl:'#2a1a12', sub:'#5a2e18' },
  frost:            { name:'Frost',         bg:'#dbeafe', ac:'#ffffff', br:'#bcd6f5',   lbl:'#1e3a5f', sub:'#5b7da6' },
  volt:             { name:'Volt',          bg:'#0b0b0d', ac:'#16161a', br:'#2a2a30',   lbl:'#ffffff', sub:'#9a9aa6' },
};

export type DigitStyle = 'segment' | 'bulb' | 'broadcast';

// Font family for layout text (team names, labels). Stacks lean on
// locally-available faces with safe fallbacks — no web-font fetch.
export type FontKey = 'default' | 'condensed' | 'mono' | 'rounded';

export const FONT_STACKS: Record<FontKey, string> = {
  default: "'Inter', system-ui, -apple-system, sans-serif",
  condensed: "'Arial Narrow', 'Roboto Condensed', 'Oswald', sans-serif",
  mono: "'SF Mono', 'Roboto Mono', ui-monospace, monospace",
  rounded: "'Nunito', 'Quicksand', ui-rounded, system-ui, sans-serif",
};

// Border treatment for zone panels (zones that carry a bg tint).
export type FrameStyle = 'flat' | 'rounded' | 'angular' | 'borderless' | 'glass';

export interface ScoreboardConfig {
  theme?: ThemeKey;
  digitStyle?: DigitStyle;
  /** When set, the scoreboard renders through the widget engine using
   *  this zone-template id instead of the hardcoded layout. */
  layoutTemplate?: string;
  schoolName?: string;
  homeLogo?: string;
  awayLogo?: string;
  sponsorTop?: { name?: string; tagline?: string };
  sponsorBottom?: { name?: string; tagline?: string };
  /** UUID of the game whose live state should drive this display. */
  assignedGameId?: string;
  /** Per-effect enable flags for the Phase 6 animation layer. Unset = on.
   *  Keyed by GameEffectType. */
  effects?: Record<string, boolean>;
}
